import  { ReactNode } from 'react';
import { adaptV4Theme } from '@mui/material/styles';
import {
  createTheme,
  Palette as MUIPalette,
  PaletteMode,
  DeprecatedThemeOptions,
  ThemeProvider as MUIThemeProvider,
  Theme,
  StyledEngineProvider,
  SimplePaletteColorOptions,
  tabClasses,
} from '@mui/material';
import {deepmerge} from '@mui/utils'
import { purple } from '@mui/material/colors';




declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}




declare module '@mui/material/styles' {
  interface Palette {
    border:SimplePaletteColorOptions
    light: string; 
    shadows: object,
    spacing : object,
    purple : object,
    hover: SimplePaletteColorOptions
  }
  interface PaletteColor { 
    main: string; 
    light: string; 
  }

  interface PaletteOptions {
    border?:SimplePaletteColorOptions,
    shadows?: object,
    spacing? : object,
    lightGray? : object,
    purple? : object,
    hover?: SimplePaletteColorOptions
  }
}

export const appColors = {
  primaryMain: '#253D82', // Example blue
  primaryLight: '#42a5f5',
  primaryDark: '#1565c0',
  secondaryMain: '#006BD7', // Example purple
  secondaryLight: '#ba68c8',
  secondaryDark: '#7b1fa2',
  errorMain: '#d32f2f',
  warningMain: '#ed6c02',
  infoMain: '#0288d1',
  successMain: '#2e7d32',
  textPrimary: 'rgba(0, 0, 0, 0.87)',
  textSecondary: 'rgba(0, 0, 0, 0.6)',
  backgroundDefault: '#f5f5f5',
  backgroundPaper: '#ffffff',
  hover: '#F3F4F3',
  textBold: '#333',
  textLight: '#666',
  textDark: '#000',
  textWhite: '#fff',
  textBlack: '#000',
  textGray: '#888',
  textLightGray: '#ccc',
};

export const spacingUnit = 8; // A base unit for consistent spacing
export const borderRadius = 4;

export const appTheme = createTheme({
  palette: {
    primary: {
      main: appColors.primaryMain,
      light: 'rgba(30, 102, 174, 0.2)',
      contrastText: '#ffffff',
    },
    secondary: {
      main: appColors.secondaryMain,
      light: '#6f7273',
    },
    error: {
      main: '#D50000',
      light: '#F23A00',
      contrastText: ' #FF3B30' 
    },
    warning: {
      main: '#ffa000',
      light: '#FBC02D'
    },
    info: {
      main: '#9C27B0',
      light: '#006BD7'
    },
    success: {  
      main: '#34C759',
     
    },
    border: {
      main: ' #79747E',
      light: '#938F99',
    }, 
    lightGray: {
      main: 'rgba(37, 61, 130, 0.04)',
    },
    purple: {
      main: purple[500],
    },
    spacing: {
        spacing: 16,
    },
    hover: {
      main: '#F3F4F3',
    },

    // shadows: ["none", "1px 1px 10px 1px rgba(0, 0, 0, 0.4)", "1px 1px 10px 1px rgba(0, 0, 0, 0.5)"], 
  },
  typography: {
    // fontFamily: 'Roboto, sans-serif',
   // htmlFontSize: 16,
    fontSize: 20,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
 
    subtitle1: {
      fontSize: 18,
      '@media (maxWidth:900px)': {
        fontSize: 16,
      }, 
    },
    subtitle2: {
      fontSize: 14,
    },
    body1: {
      fontSize: 16,
    },
    body2: {
      fontSize: 14,
    },
    button: {
      fontSize: 12,
    },
    caption: {
      fontSize: 12,
      color: 'rgba(0, 0, 0, 0.6)',
    },
    overline: {
      fontSize: 12,
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 767,
      md: 900,
      lg: 1200,
      xl: 1536,
    }
  },
  components: {
    // change the body properties (also see index.css)
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          accentColor: appColors.primaryMain,
          margin: '0 auto',
        },
        h1: {
          borderBottom: "1px solid #D3D3D4",
          paddingBottom: "10px",
        },
      }
    },
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: "2em",
          fontWeight: 700,
          margin: "1em 1em 1em 0",
        },
        h2: {
          fontSize: "1.5em",
          fontWeight: 700,
          margin: "1em 1em 1em 0",
        },
        h3: {
          fontSize: "1.2em",
          fontWeight: 700,
          margin: "1em 1em 1em 0",
        },
        h4: {
          fontSize: "1em",
          fontWeight: 700,
          margin: "1em 1em 1em 0",
        },
        h5: {
          fontSize: "1em",
          fontWeight: 700,
          margin: "1em 1em 1em 0",
        },
        h6: {
          fontSize: "1em",
          fontWeight: 700,
          margin: "1em 1em 1em 0",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: "#eee",
          borderRadius: "8px",
          minHeight: 32,
        },
        flexContainer: {
          position: "relative",
          padding: "0 3px",
          zIndex: 1,
        },
        indicator: {
          top: 3,
          bottom: 3,
          right: 3,
          height: "auto",
          borderRadius: "7px",
          backgroundColor: "#fff",
          boxShadow: "0 4px 12px 0 rgba(0,0,0,0.16)",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          minHeight: 32,
          minWidth: 200,
          opacity: 0.7,
          padding: "7px 16px",
          textTransform: "initial",
          "&:hover": {
            opacity: 1,
          },
          [`&.${tabClasses.selected}`]: {
            opacity: 1,
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: 20,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        InputLabelProps: { shrink: true },
        fullWidth: true,
        variant: "outlined",
        size: "small",
        sx: {
              backgroundColor: '#ffffff',
              borderRadius: 2,
        }
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: {
          padding: "0 5px",
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderRadius: '8px',
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: '0px 10px 0px 10px',
          '&:hover': {
            backgroundColor: appColors.hover,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: '0px 0px 30px 0px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
          padding: '2px',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: 'none',
          "&.Mui-selected": {
            backgroundColor: 'white',
            color: appColors.primaryMain,
            boxShadow: '1px 1px 0px 1px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          "&.MuiToggleButton-root.Mui-selected": {
            margin: '2px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        outlined: {
          '&:hover': {
            color: '#ffffff',
            backgroundColor: appColors.primaryMain,
          },
        },
        root: {
          '&.Mui-disabled': {
            color: 'rgba(0, 0, 0, 0.46)',
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
  }
})

export const getThemeOptions = (mode: PaletteMode = 'light') => {
  return appTheme;
}


type ThemeProviderProps = {
  mode: PaletteMode
  children: ReactNode |
  ((theme: DeprecatedThemeOptions & {
    palette: MUIPalette
  }) => ReactNode)
}


export const ThemeProvider = ({ mode, children }: ThemeProviderProps) => {
  let theme = getThemeOptions(mode);
  theme = createTheme(adaptV4Theme(theme));
  
  return (
    <MUIThemeProvider theme={theme}>
      {typeof children === 'function' ? children(theme) : children}
    </MUIThemeProvider>
  )
};
